/**
 * Seedance 2.0 AI Video Generation Constants
 *
 * Seedance 2.0 (ByteDance) via fal.ai - top-rated AI video generation.
 * Pricing is per second:
 *   - Fast:     $0.2419 / sec at 720p (audio included)
 *   - Standard: $0.3034 / sec at 720p (audio included)
 *   - R2V with video_url input: 0.6x multiplier
 *
 * Tokens are derived from those vendor costs by `tokenBilling.ts`, the port of
 * `constants/pricing/base.py::cost_usd_to_billed_tokens` -- never by a markup
 * written out here. This file used to carry `const MARKUP_MULTIPLIER = 1.5` and a
 * second local `usdToTokens`; the backend markup had already moved to 2.0, so
 * every Seedance quote was 25% under what the server charged, and the margin
 * floor was skipped entirely.
 */

import { requireRate, videoCostUsdToTokens as usdToTokens } from './tokenBilling';

export const SEEDANCE2_FUNCTION_MODES = {
  TEXT_TO_VIDEO: 'text_to_video',
  IMAGE_TO_VIDEO: 'image_to_video',
  REFERENCE_TO_VIDEO: 'reference_to_video',
} as const;

export type Seedance2FunctionMode =
  (typeof SEEDANCE2_FUNCTION_MODES)[keyof typeof SEEDANCE2_FUNCTION_MODES];

export const SEEDANCE2_QUALITIES = ['fast', 'standard'] as const;
export type Seedance2Quality = (typeof SEEDANCE2_QUALITIES)[number];

export const SEEDANCE2_DURATIONS = ['4', '5', '8', '10', '15'] as const;
export type Seedance2Duration = (typeof SEEDANCE2_DURATIONS)[number];

export const SEEDANCE2_RESOLUTIONS = ['480p', '720p'] as const;
export type Seedance2Resolution = (typeof SEEDANCE2_RESOLUTIONS)[number];

export const SEEDANCE2_ASPECT_RATIOS = ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'] as const;
export type Seedance2AspectRatio = (typeof SEEDANCE2_ASPECT_RATIOS)[number];

const SEEDANCE2_PRICE_PER_SECOND_USD: Record<Seedance2Quality, number> = {
  fast: 0.2419,
  standard: 0.3034,
};

export const SEEDANCE2_QUALITY_OPTIONS = [
  {
    value: 'fast' as const,
    label: 'Fast',
    description: '$0.2419/sec - faster generation, great quality',
    tokens5s: usdToTokens(SEEDANCE2_PRICE_PER_SECOND_USD.fast * 5),
    tokens10s: usdToTokens(SEEDANCE2_PRICE_PER_SECOND_USD.fast * 10),
  },
  {
    value: 'standard' as const,
    label: 'Standard',
    description: '$0.3034/sec - highest quality output',
    tokens5s: usdToTokens(SEEDANCE2_PRICE_PER_SECOND_USD.standard * 5),
    tokens10s: usdToTokens(SEEDANCE2_PRICE_PER_SECOND_USD.standard * 10),
  },
] as const;

export const SEEDANCE2_DURATION_OPTIONS = [
  { value: '4' as const, label: '4 seconds' },
  { value: '5' as const, label: '5 seconds' },
  { value: '8' as const, label: '8 seconds' },
  { value: '10' as const, label: '10 seconds' },
  { value: '15' as const, label: '15 seconds' },
] as const;

export const SEEDANCE2_RESOLUTION_OPTIONS = [
  { value: '480p' as const, label: '480p SD' },
  { value: '720p' as const, label: '720p HD' },
] as const;

export const SEEDANCE2_ASPECT_RATIO_OPTIONS = [
  { value: '21:9' as const, label: 'Ultra-wide (21:9)' },
  { value: '16:9' as const, label: 'Landscape (16:9)' },
  { value: '4:3' as const, label: 'Classic (4:3)' },
  { value: '1:1' as const, label: 'Square (1:1)' },
  { value: '3:4' as const, label: 'Portrait (3:4)' },
  { value: '9:16' as const, label: 'Vertical (9:16)' },
] as const;

export const SEEDANCE2_INPUT_LIMITS = {
  MAX_IMAGE_REFS: 9,
  MAX_VIDEO_REFS: 3,
  MAX_AUDIO_REFS: 3,
} as const;

export interface Seedance2Config {
  function_mode: Seedance2FunctionMode;
  quality: Seedance2Quality;
  duration: Seedance2Duration;
  resolution: Seedance2Resolution;
  aspect_ratio: Seedance2AspectRatio;
  generate_audio: boolean;
}

export const DEFAULT_SEEDANCE2_CONFIG: Seedance2Config = {
  function_mode: 'text_to_video',
  quality: 'fast',
  duration: '5',
  resolution: '720p',
  aspect_ratio: '16:9',
  generate_audio: true,
};

/**
 * Estimate token cost for a Seedance 2.0 generation.
 * Mirrors backend calculate_seedance2_cost():
 * - Per-second pricing
 * - R2V with video input gets 0.6x multiplier
 * - markup + margin floor via cost_usd_to_billed_tokens
 */
export function calculateSeedance2Tokens(
  duration: Seedance2Duration,
  quality: Seedance2Quality,
  hasVideoRef: boolean = false
): number {
  const seconds = parseInt(duration, 10);
  const rate = requireRate(SEEDANCE2_PRICE_PER_SECOND_USD, quality, 'Seedance 2.0 per-second');
  let usd = rate * seconds;
  if (hasVideoRef) {
    usd *= 0.6;
  }
  return usdToTokens(usd);
}
